"use client"

import { useEffect } from "react"
import type { Room } from "@workspace/livekit/client"
import { z } from "zod"
import type { ToolCallEvent, VoiceboxTools } from "@/schemas"

const ToolRpcPayloadSchema = z.object({
  tool: z.string().min(1),
  arguments: z.unknown(),
})

type ToolRpcPayload = z.infer<typeof ToolRpcPayloadSchema>

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function parsePayload(payload: string): ToolRpcPayload {
  let parsed: unknown
  try {
    parsed = JSON.parse(payload)
  } catch {
    throw new Error("Invalid tool_call payload: expected JSON.")
  }

  if (!isObjectRecord(parsed)) {
    throw new Error("Invalid tool_call payload: expected object.")
  }

  const result = ToolRpcPayloadSchema.safeParse(parsed)
  if (!result.success) {
    throw new Error(`Invalid tool_call payload: ${result.error.message}`)
  }

  return result.data
}

interface UseToolRpcOptions {
  room: Room
  tools?: VoiceboxTools
  onToolCall?: (event: ToolCallEvent) => void
}

export function useToolRpc({ room, tools, onToolCall }: UseToolRpcOptions) {
  useEffect(() => {
    if (!tools || room.state !== "connected") return

    room.registerRpcMethod("tool_call", async (data) => {
      const payload = parsePayload(data.payload)
      const toolDefinition = tools[payload.tool]

      if (!toolDefinition) {
        throw new Error(`Unknown tool '${payload.tool}'.`)
      }

      const parsedArgs = await toolDefinition.inputSchema.safeParseAsync(
        payload.arguments
      )
      if (!parsedArgs.success) {
        throw new Error(
          `Invalid arguments for tool '${payload.tool}': ${parsedArgs.error.message}`
        )
      }

      const result = await toolDefinition.execute(parsedArgs.data)
      const response =
        typeof result === "string" ? result : JSON.stringify(result)
      onToolCall?.({
        toolId: payload.tool,
        toolArgs: parsedArgs.data,
        response,
      })
      return response
    })

    return () => {
      room.unregisterRpcMethod?.("tool_call")
    }
  }, [room, room.state, tools])
}
